import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { AppModuleDefinition } from '../../types';

interface TabsDrawerProps {
  isCollapsed: boolean;
  searchQuery: string;
  availableModules: AppModuleDefinition[];
  placedModules: AppModuleDefinition[];
  onToggleCollapsed: () => void;
  onSearchChange: (value: string) => void;
  onAddModule: (moduleId: AppModuleDefinition['id']) => void;
  onRemoveModule: (moduleId: AppModuleDefinition['id']) => void;
  onResetBoard: () => void;
  isAdmin: boolean;
}

const TabsDrawer: React.FC<TabsDrawerProps> = ({
  isCollapsed,
  searchQuery,
  availableModules,
  placedModules,
  onToggleCollapsed,
  onSearchChange,
  onAddModule,
  onRemoveModule,
  onResetBoard,
  isAdmin,
}) => {
  const filteredAvailableModules = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return availableModules;
    }

    return availableModules.filter((module) =>
      [module.title, module.description, ...module.tags].some((value) =>
        value.toLowerCase().includes(normalizedQuery)
      )
    );
  }, [availableModules, searchQuery]);

  return (
    <aside
      className="dashboard-panel-surface sticky h-fit w-full overflow-hidden rounded-3xl transition-all duration-300 ease-out xl:w-80"
      style={{ top: 'var(--app-drawer-offset, 154px)' }}
    >
      <div className="dashboard-panel-divider border-b p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="dashboard-card-title">Tabs</h2>
            <p className="dashboard-shell-subtitle">Dashboard app drawer</p>
          </div>
          <div className="dashboard-module-controls flex shrink-0 items-center gap-2">
            {!isCollapsed && (
              <button
                type="button"
                disabled={!isAdmin}
                onClick={onResetBoard}
                className={`dashboard-button-neutral-muted flex h-9 w-9 items-center justify-center rounded-xl ${
                  !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                }`}
                aria-label="Reset board"
                title="Reset board"
              >
                <RotateCcw className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="dashboard-button-neutral-muted flex h-9 w-9 items-center justify-center rounded-xl"
              aria-label={isCollapsed ? 'Open tabs drawer' : 'Close tabs drawer'}
              title={isCollapsed ? 'Open' : 'Close'}
            >
              {isCollapsed ? (
                <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
              ) : (
                <ChevronUp className="h-4 w-4" strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        }`}
      >
        <div className="space-y-3 p-3">
          <div>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search tabs..."
              className="dashboard-input-surface dashboard-body-text h-9 w-full rounded-2xl px-3"
            />
          </div>

          <section className="space-y-3">
            <h3 className="dashboard-section-label">Placed Apps</h3>
            <div className="space-y-2">
              {placedModules.length === 0 ? (
                <div className="dashboard-surface-soft dashboard-muted-body rounded-2xl p-3">
                  No tabs on the board yet.
                </div>
              ) : (
                placedModules.map((module) => (
                  <div key={module.id} className="dashboard-surface-strong rounded-2xl p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="dashboard-badge-accent dashboard-badge-label rounded-full px-2 py-0.5">
                            {module.icon}
                          </span>
                          <span className="dashboard-body-text dashboard-text-strong font-bold">{module.title}</span>
                        </div>
                        <p className="dashboard-meta-text mt-1">{module.description}</p>
                      </div>
                      <button
                        type="button"
                        disabled={!isAdmin}
                        onClick={() => onRemoveModule(module.id)}
                        className={`dashboard-button-destructive flex h-9 w-9 items-center justify-center rounded-xl ${
                          !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                        }`}
                        aria-label={`Remove ${module.title}`}
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="dashboard-section-label">Available Apps</h3>
            <div className="space-y-2">
              {filteredAvailableModules.length === 0 ? (
                <div className="dashboard-surface-soft dashboard-muted-body rounded-2xl p-3">
                  No matching tabs found.
                </div>
              ) : (
                filteredAvailableModules.map((module) => (
                  <div key={module.id} className="dashboard-surface-strong rounded-2xl p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="dashboard-badge-accent dashboard-badge-label rounded-full px-2 py-0.5">
                            {module.icon}
                          </span>
                          <span className="dashboard-body-text dashboard-text-strong font-bold">{module.title}</span>
                        </div>
                        <p className="dashboard-meta-text mt-1">{module.description}</p>
                      </div>
                      <button
                        type="button"
                        disabled={!isAdmin}
                        onClick={() => onAddModule(module.id)}
                        className={`dashboard-button-accent flex h-9 w-9 items-center justify-center rounded-xl ${
                          !isAdmin ? 'cursor-not-allowed opacity-60' : ''
                        }`}
                        aria-label={`Add ${module.title}`}
                        title="Add"
                      >
                        <Plus className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </aside>
  );
};

export default TabsDrawer;
